import { z } from 'zod/v4';

// A woman's story ("Ela chegou") is an ordered list of cards stored as JSON on
// the `women` row. The FIRST page (reveal) and the LAST page (closing) are fixed
// and rendered by the viewer — they are NOT part of this list.

// Text stored in English AND Portuguese; either side may be ''.
export const localizedSchema = z.object({
  en: z.string(),
  pt: z.string(),
});

// One vocabulary pair shown under a card (English word → Portuguese meaning).
export const vocabPairSchema = z.object({
  en: z.string(),
  pt: z.string(),
});

export const STORY_CARD_TYPES = ['text', 'video', 'notebook', 'quote'] as const;
export const storyCardTypeEnum = z.enum(STORY_CARD_TYPES);

export const STORY_CARD_THEMES = ['ink', 'red', 'paper'] as const;
export const storyCardThemeEnum = z.enum(STORY_CARD_THEMES);

export const STORY_VIDEO_SOURCES = ['upload', 'reference'] as const;
export const storyVideoSourceEnum = z.enum(STORY_VIDEO_SOURCES);

export const STORY_CARDS_MAX = 12;
export const TEXT_CARD_VOCAB_MAX = 3;

// Every card has an id and an optional free small heading (`title`).
const baseStoryCardSchema = z.object({
  id: z.string(),
  title: localizedSchema.optional(),
});

// Text card. `body` supports *word* → highlighted; `big` is the large outlined
// word/number (e.g. "1932").
export const textStoryCardSchema = baseStoryCardSchema.extend({
  type: z.literal('text'),
  big: z.string().optional(),
  theme: storyCardThemeEnum,
  body: localizedSchema,
  vocab: z.array(vocabPairSchema).max(TEXT_CARD_VOCAB_MAX).optional(),
});

// Video card. `url` is the uploaded file URL, or a YouTube/Vimeo/Loom/mp4
// reference when `source` is 'reference'.
export const videoStoryCardSchema = baseStoryCardSchema.extend({
  type: z.literal('video'),
  source: storyVideoSourceEnum,
  url: z.string(),
  posterUrl: z.string().optional(),
  caption: localizedSchema.optional(),
});

// A page "from her notebook": her own words in the original language plus a
// simple gloss.
export const notebookStoryCardSchema = baseStoryCardSchema.extend({
  type: z.literal('notebook'),
  date: localizedSchema.optional(),
  original: z.string(),
  originalLang: z.enum(['en', 'pt']),
  source: localizedSchema.optional(),
  gloss: localizedSchema.optional(),
  vocab: z.array(vocabPairSchema).optional(),
});

// Shows the woman's quote + source (from her profile) and the seal promise;
// `body` is an optional intro line.
export const quoteStoryCardSchema = baseStoryCardSchema.extend({
  type: z.literal('quote'),
  body: localizedSchema.optional(),
});

export const storyCardSchema = z.discriminatedUnion('type', [
  textStoryCardSchema,
  videoStoryCardSchema,
  notebookStoryCardSchema,
  quoteStoryCardSchema,
]);

export const storyCardsSchema = z.array(storyCardSchema).max(STORY_CARDS_MAX);

// A research source the AI draft was based on.
export const storySourceSchema = z.object({
  title: z.string(),
  url: z.string(),
});

export const WOMAN_STORY_STATUSES = ['draft', 'published'] as const;
export const womanStoryStatusEnum = z.enum(WOMAN_STORY_STATUSES);

// Status of an AI job on a woman (portrait generation / profile draft).
export const WOMAN_JOB_STATUSES = ['idle', 'pending', 'generating', 'generated', 'failed'] as const;
export const womanJobStatusEnum = z.enum(WOMAN_JOB_STATUSES);

export type Localized = z.infer<typeof localizedSchema>;
export type VocabPair = z.infer<typeof vocabPairSchema>;
export type StoryCardType = (typeof STORY_CARD_TYPES)[number];
export type StoryCardTheme = (typeof STORY_CARD_THEMES)[number];
export type StoryVideoSource = (typeof STORY_VIDEO_SOURCES)[number];
export type TextStoryCard = z.infer<typeof textStoryCardSchema>;
export type VideoStoryCard = z.infer<typeof videoStoryCardSchema>;
export type NotebookStoryCard = z.infer<typeof notebookStoryCardSchema>;
export type QuoteStoryCard = z.infer<typeof quoteStoryCardSchema>;
export type StoryCard = z.infer<typeof storyCardSchema>;
export type StorySource = z.infer<typeof storySourceSchema>;
export type WomanStoryStatus = (typeof WOMAN_STORY_STATUSES)[number];
export type WomanJobStatus = (typeof WOMAN_JOB_STATUSES)[number];
